import { useEffect, useState } from "react";

const STORAGE_KEY = "ultra-mesure-session";
const TOKEN_KEY = "ultra-mesure-token";

interface CachedSession {
  user: {
    id: string;
    email: string;
    name?: string | null;
    role?: string;
  };
}

/** Lecture synchrone de localStorage (pas de useEffect).
 *  Si une session existe sans token Bearer, c'est un vestige
 *  de l'ancienne auth NextAuth → on la nettoie automatiquement. */
function readCachedSession(): CachedSession | null {
  try {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    // Vestige post-migration : session sans token Bearer → forcer re-login
    const hasToken = !!localStorage.getItem(TOKEN_KEY);
    if (!hasToken) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Hook de session compatible offline.
 * - Online : vérifie le token JWT, sauvegarde dans localStorage
 * - Offline : utilise la session cachée dans localStorage
 *
 * IMPORTANT : la session cachée est lue de manière SYNCHRONE au premier render
 * pour éviter un flash "unauthenticated" avant que le useEffect ait lu le cache.
 */
export function useOfflineSession() {
  // Lecture synchrone au premier render (pas de useEffect)
  const [cachedSession, setCachedSession] = useState<CachedSession | null>(
    readCachedSession
  );
  const [isLoading, setIsLoading] = useState(() => {
    // Si on a une session cached, on n'est pas en loading
    return readCachedSession() === null && !!localStorage.getItem(TOKEN_KEY);
  });

  // Vérifier le token côté serveur si online
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }

    // Si on a déjà une session cached, pas en loading
    // mais on vérifie quand même le token en arrière-plan
    fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.user) {
          const toCache: CachedSession = { user: data.user };
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toCache));
          } catch (err) {
            console.warn("[auth] Échec mise en cache de la session (quota ?)", err);
          }
          setCachedSession(toCache);
        }
      })
      .catch(() => {
        // Offline ou erreur — on garde la session cachée
      })
      .finally(() => setIsLoading(false));
  }, []);

  const session = cachedSession;
  const isAuthenticated = !!session?.user;

  return { session, isLoading, isAuthenticated };
}

/**
 * Efface la session cachée (à appeler au logout).
 */
export function clearCachedSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Sauvegarde la session dans localStorage.
 */
export function saveCachedSession(user: CachedSession["user"]) {
  try {
    const session: CachedSession = { user };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (err) {
    console.warn("[auth] Échec sauvegarde session en localStorage (quota ?)", err);
  }
}

/**
 * Sauvegarde le JWT token.
 *
 * COMPROMIS ASSUMÉ (I2) : le token est en localStorage, donc exfiltrable par XSS.
 * C'est un choix offline-first : un cookie httpOnly ne permettrait pas le login
 * offline (le service worker / l'app doivent lire le token sans réseau). Le rempart
 * anti-XSS est la CSP `script-src 'self'` posée côté serveur Hono (cf. server/src/index.ts),
 * qui empêche l'exécution de tout script injecté.
 */
export function saveToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (err) {
    console.warn("[auth] Échec sauvegarde du token (quota ?)", err);
  }
}

/**
 * Récupère le JWT token.
 */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

// ─── Login offline ─────────────────────────────────────────────

const OFFLINE_CREDS_KEY = "ultra-mesure-offline-credentials";

interface OfflineCredentials {
  email: string;
  passwordHash: string;
  user: CachedSession["user"];
  // Format PBKDF2 (depuis Lot 7). Absents => ancien format SHA-256 non salé,
  // migré en place à la première connexion offline réussie.
  algo?: "PBKDF2";
  salt?: string; // hex, 16 octets aléatoires par appareil
  iterations?: number;
}

// PBKDF2-HMAC-SHA256. 210k itérations = compromis sécurité/perf sur tablette
// terrain (le login offline est rare). Le sel par appareil casse les rainbow-tables.
const PBKDF2_ITERATIONS = 210_000;
const SALT_BYTES = 16;

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function randomSaltHex(): string {
  return toHex(crypto.getRandomValues(new Uint8Array(SALT_BYTES)));
}

/** Comparaison à temps constant (évite le timing-attack sur le hash). */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/** Dérive le hash PBKDF2-SHA256 du mot de passe avec le sel par appareil. */
export async function derivePbkdf2(
  password: string,
  saltHex: string,
  iterations: number
): Promise<string> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: hexToBytes(saltHex) as BufferSource,
      iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    256
  );
  return toHex(new Uint8Array(bits));
}

/** Ancien hash SHA-256 non salé — conservé UNIQUEMENT pour migrer les creds existants. */
async function legacySha256(password: string): Promise<string> {
  const data = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return toHex(new Uint8Array(hashBuffer));
}

export async function saveOfflineCredentials(
  email: string,
  password: string,
  user: CachedSession["user"]
): Promise<void> {
  try {
    const salt = randomSaltHex();
    const passwordHash = await derivePbkdf2(password, salt, PBKDF2_ITERATIONS);
    const creds: OfflineCredentials = {
      email,
      passwordHash,
      user,
      algo: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
    };
    localStorage.setItem(OFFLINE_CREDS_KEY, JSON.stringify(creds));
  } catch (err) {
    console.warn("[auth] Échec sauvegarde des credentials offline (quota ?)", err);
  }
}

export async function tryOfflineLogin(
  email: string,
  password: string
): Promise<CachedSession | null> {
  try {
    const stored = localStorage.getItem(OFFLINE_CREDS_KEY);
    if (!stored) return null;

    const creds: OfflineCredentials = JSON.parse(stored);
    if (creds.email !== email) return null;

    if (creds.algo === "PBKDF2" && creds.salt && creds.iterations) {
      const inputHash = await derivePbkdf2(password, creds.salt, creds.iterations);
      if (!timingSafeEqual(inputHash, creds.passwordHash)) return null;
    } else {
      // Ancien format SHA-256 non salé : on vérifie puis on migre en place vers
      // PBKDF2 (on a le mot de passe en clair ici, migration transparente).
      const legacy = await legacySha256(password);
      if (!timingSafeEqual(legacy, creds.passwordHash)) return null;
      await saveOfflineCredentials(email, password, creds.user);
    }

    const session: CachedSession = { user: creds.user };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}
